import React, { useEffect, useState, useRef, useCallback } from 'react';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import { connect } from 'react-redux';
import Button from 'react-bootstrap/Button';
import CheckIcon from '@material-ui/icons/Check';
import PropTypes from 'prop-types';
import {
  apiValidation,
  copyToClipboardHybrid,
  get,
  getAvatarForUser,
  isWebView,
  timestampToWhatsappFormat,
  post,
} from '../../../Utilities';
import './Comments.scss';
import { getClientUserId, getRoleArray } from '../../../redux/reducers/clientUserId.reducer';
import { getUserProfile } from '../../../redux/reducers/userProfile.reducer';
import { IconButton, Switch } from '@mui/material';
import { KeyboardArrowDown, Send } from '@mui/icons-material';
import InitialsAvatar from '../../Login/Preloader/InitialsAvatar';

const Comments = (props) => {
  const { clientUserId, fileId, streamId, userProfile, roleArray } = props;
  const [comments, setComments] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [allowComments, setAllowComments] = useState('true');
  const loaded = useRef(null);
  // Incremental chat polling (phase A): after the first full load, every poll sends
  // `since` = the newest created_at seen so far, so the server returns only NEW
  // comments and they get appended instead of re-rendering the whole list.
  const commentsRef = useRef([]); // mirror of `comments` — readable inside interval callbacks without a stale closure
  const sinceRef = useRef(null); // null = next poll is a full load (initial state)
  const seenRef = useRef(new Set()); // keys of server rows already shown (the >= cursor can re-deliver the last row)
  const POLL_INTERVAL_MS = 5000;

  const end = useRef(null);
  const footerRef = useRef(null);

  const adjustFooterHeight = useCallback(() => {
    if (footerRef.current) {
      const rect = footerRef.current.getBoundingClientRect();
      const distanceToBottom = window.innerHeight - rect.top;
      const newHeight = distanceToBottom - 80;
      footerRef.current.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    adjustFooterHeight();
    window.addEventListener('resize', adjustFooterHeight);
    return () => window.removeEventListener('resize', adjustFooterHeight);
  }, [adjustFooterHeight]);

  const scrollToBottom = () => {
    end.current.scrollIntoView({ behavior: 'smooth' });
  };

  const setCommentsList = (next) => {
    commentsRef.current = next;
    setComments(next);
  };

  // created_at is second-granularity and the cursor is inclusive (>=), so the last
  // comment can legitimately be re-delivered by polls — dedupe on the composite key.
  const commentKey = (c) =>
    `${c.client_user_id}|${c.created_at}|${c.chat_text}`;

  const applyServerComments = (rows) => {
    if (!Array.isArray(rows)) return;

    if (sinceRef.current === null) {
      // First (full) load — replace everything, exactly the old behavior.
      seenRef.current = new Set(rows.map(commentKey));
      setCommentsList(rows);
    } else if (rows.length) {
      const fresh = [];
      for (const c of rows) {
        const key = commentKey(c);
        if (seenRef.current.has(key)) continue;
        seenRef.current.add(key);
        fresh.push(c);
      }
      if (fresh.length) {
        // A confirmed copy of the sender's still-optimistic message replaces that
        // bubble (it has the real id/timestamp); the optimistic flag marks them.
        let list = commentsRef.current;
        for (const c of fresh) {
          if (Number(c.client_user_id) === Number(clientUserId)) {
            list = list.filter(
              (p) => !(p.optimistic && p.chat_text === c.chat_text),
            );
          }
        }
        setCommentsList([...list, ...fresh]);
      }
    }

    // Advance the cursor to the newest created_at seen (stays put on an empty
    // poll). With the inclusive cursor + dedupe above, a comment created in the
    // same second as the cursor is re-delivered and shown once — never missed.
    for (const c of rows) {
      const ts = Number(c.created_at);
      if (Number.isFinite(ts) && (sinceRef.current === null || ts > sinceRef.current)) {
        sinceRef.current = ts;
      }
    }
  };

  const getComments = useCallback(() => {
    let result = [];
    if (fileId) {
      const params = { file_id: fileId };
      if (sinceRef.current !== null) {
        params.since = sinceRef.current;
      }
      get(params, '/getCommentsOfVideo').then((res) => {
        if (res && res.success) {
          result = apiValidation(res);
          if (result) {
            applyServerComments(result);
            const objDiv = document.getElementById('messages');

            if (objDiv && objDiv.scrollHeight - objDiv.scrollTop < 800) {
              objDiv.scrollTop = objDiv.scrollHeight;
            } else if (!loaded.current) {
              loaded.current = true;
              const objDiv = document.getElementById('messages');
              if (objDiv) {
                objDiv.scrollTop = objDiv.scrollHeight;
              }
            }
          }
        }
      });
    }
  }, [fileId, clientUserId]);

  const getCommentsYoutubeStream = useCallback(() => {
    let result = [];
    if (streamId) {
      const params = { stream_id: streamId };
      if (sinceRef.current !== null) {
        params.since = sinceRef.current;
      }
      get(params, '/getCommentsOfYoutubeLiveStream').then((res) => {
        if (res && res.success) {
          result = apiValidation(res);
          if (result) {
            const objDiv = document.getElementById('messages');

            applyServerComments(result);
            setAllowComments(res.allow_comments);

            if (objDiv && objDiv.scrollHeight - objDiv.scrollTop < 800) {
              objDiv.scrollTop = objDiv.scrollHeight;
            } else if (!loaded.current) {
              loaded.current = true;
              const objDiv = document.getElementById('messages');
              if (objDiv) {
                objDiv.scrollTop = objDiv.scrollHeight;
              }
            }
          }
        }
      });
    }
  }, [streamId, clientUserId]);

  useEffect(() => {
    let clear;
    const fetchFn = fileId ? getComments : getCommentsYoutubeStream;

    if (fileId || streamId) {
      fetchFn();
      clear = setInterval(() => {
        // tabs the student has switched away from stop hitting the API entirely;
        // a hidden-tab catch-up happens the moment they come back (listener below)
        if (document.visibilityState === 'visible') {
          fetchFn();
        }
      }, POLL_INTERVAL_MS);
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          fetchFn();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      return () => {
        clearInterval(clear);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }
    return undefined;
  }, [fileId, streamId, getComments, getCommentsYoutubeStream]);

  const addComment = () => {
    let payload = {};

    if (fileId) {
      payload = {
        client_user_id: clientUserId,
        file_id: fileId,
        chat_text: currentInput,
      };
    } else if (streamId) {
      payload = {
        client_user_id: clientUserId,
        stream_id: streamId,
        comment_text: currentInput,
      };
    }

    post(payload, fileId ? '/addComment' : '/addCommentYoutubeStream').then((res) => {
      setCurrentInput('');

      // Optimistic bubble — tagged with `optimistic` so the poller can replace it
      // (and only it) with the server's confirmed row once the incremental poll
      // delivers it, instead of leaving a duplicate of the same message on screen.
      const newComments = [
        ...commentsRef.current,
        {
          optimistic: true,
          client_user_id: clientUserId,
          video_chat_id: 0,
          created_at: Math.ceil(Date.now() / 1000),
          first_name: userProfile.firstName,
          last_name: userProfile.lastName,
          profile_image: userProfile.profileImage,
          chat_text: currentInput,
        },
      ];
      setCommentsList(newComments);
      // scrollToBottom();
      const objDiv = document.getElementById('messages');
      objDiv.scrollTop = objDiv.scrollHeight;
    });
  };

  const goToBottom = () => {
    const objDiv = document.getElementById('messages');
    objDiv.scrollTo({
      top: objDiv.scrollHeight,
      behavior: 'smooth',
    });
  };

  const toggleCommentPermission = () => {
    setAllowComments((prev) => (prev === 'true' ? 'false' : 'true'));
    post(
      { allow_comments: allowComments === 'true' ? 'false' : 'true', stream_id: streamId },
      '/changeYoutubeStreamCommentPermission',
    ).then((res) => {
      if (res && res.success) {
        console.log('updated');
        setAllowComments(res.result);
      }
    });
  };

  const isTeacherAdmin = roleArray.includes(3) || roleArray.includes(4) || roleArray.includes(5);

  return (
    <div className='comments__desktopWidth'>
      {isTeacherAdmin && streamId ? (
        <div className='mx-0 my-3'>
          <Switch checked={allowComments === 'true'} onChange={() => toggleCommentPermission()} />
          <span className='font'>
            {allowComments === 'true'
              ? 'Student comments are enabled'
              : 'Student comments are disabled'}
          </span>
        </div>
      ) : null}
      {allowComments === 'true' && <p className='mb-0 fontBold mx-2 mt-3'>Comments</p>}
      <div
        className='d-flex mx-0 mb-3 mt-2 p-2 messagesContainer'
        id='messages'
        ref={footerRef}
        onScroll={(event) => {
          if (event.target.scrollHeight - event.target.scrollTop < 800) {
            setIsScrolled(false);
          } else {
            setIsScrolled(true);
          }
        }}
      >
        {comments.length > 0 ? (
          comments.map((e) => {
            return (
              <div
                key={e.video_chat_id}
                className={
                  'd-flex m-2'
                }
              >
                <InitialsAvatar
                  name={`${e.first_name} ${e.last_name}`}
                  className='mt-0'
                  size='sm'
                  color={getAvatarForUser(e.client_user_id)}
                />
                <div className='d-flex mx-2 w-75' style={{ flexDirection: 'column' }}>
                  <span style={{ fontFamily: 'Montserrat-Regular', fontSize: '10px' }}>
                    {`${e.first_name} ${e.last_name}`} • {timestampToWhatsappFormat(e.created_at)}
                  </span>
                  <span
                    style={{
                     
                      borderRadius: '5px',
                      padding: '0px',
                      textAlign: 'left',
                      fontSize: '14px',
                      fontFamily: 'Montserrat-Medium',
                      textTransform: 'capitalize',
                      color: e.client_user_id === clientUserId ? 'var(--material-dark)' : 'rgba(0, 0, 0, 0.87)',
                    }}
                  >
                    {e.chat_text}
                  </span>
                  {/* <span style={{ fontFamily: 'Montserrat-Regular', fontSize: '10px' }}>
                    {format(fromUnixTime(e.created_at), 'kk:m MMM dd, yyyy')}
                  </span> */}
                </div>
              </div>
            );
          })
        ) : (
          <p className='mx-2 mt-5 mb-0 text-center' style={{ fontFamily: 'Montserrat-SemiBold' }}>
            No comments yet
            <br /><span style={{fontSize: '12px', fontFamily: 'Montserrat-Regular'}}>Say something to start the conversation</span>
          </p>
        )}
        <div ref={end} />
        {allowComments === 'true' || isTeacherAdmin ? (
          <footer className='mt-4' style={{ position: 'relative', width: '100%' }}>
            <div className='d-flex justify-content-between px-2 w-100 comments__textarea'>
              <label className={`has-float-label my-auto ${currentInput.length===0 ? 'w-100' : 'w-85'}`}>
                <textarea
                  style={{ height: '42px', borderRadius: '100px' }}
                  className='form-control'
                  name='Add Comment'
                  type='text'
                  rows={1}
                  placeholder='Add Comment'
                  value={currentInput}
                  onFocus={() => {
                    copyToClipboardHybrid('_', false);
                  }}
                  onChange={(e) => setCurrentInput(e.target.value)}
                />
                <span>Add Comment</span>
              </label>
             {currentInput.length>0 && <IconButton
                style={{background: 'var(--primary-blue)', width: '42px', color: 'white'}}
                disabled={!currentInput}
                onClick={() => addComment()}
              >
                <Send />
              </IconButton>}
            </div>
            {isScrolled && (
              <div style={{ position: 'fixed', bottom: '80px' }} className='w-100 text-center'>
                <IconButton style={{ background: '#eee' }} onClick={goToBottom}>
                  <KeyboardArrowDown />
                </IconButton>
              </div>
            )}
          </footer>
        ) : (
          <div className='my-4 mx-auto w-100 text-center font'>
            Comments are turned off for this stream
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  clientUserId: getClientUserId(state),
  userProfile: getUserProfile(state),
  roleArray: getRoleArray(state),
});

export default connect(mapStateToProps)(Comments);

Comments.propTypes = {
  clientUserId: PropTypes.number.isRequired,
  fileId: PropTypes.number.isRequired,
};
